import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  BellRing,
  CalendarClock,
  Check,
  FileCheck2,
  FileText,
  MessageSquareText,
  Phone,
  Receipt,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { HERO_FEED, HERO_SCHEDULE } from '../data/site';

const AGENT_ICONS = {
  Reception: Phone,
  Insurance: ShieldCheck,
  Scheduling: CalendarClock,
  Claims: FileCheck2,
  'EOB posting': Receipt,
  Denials: FileText,
  Recall: BellRing,
  Billing: MessageSquareText,
};

const SLOT_PILLS = {
  confirmed: { label: 'Confirmed', tone: 'a6-pill-green' },
  booked: { label: 'Booked by AI', tone: 'a6-pill-blue' },
  verified: { label: 'Benefits verified', tone: 'a6-pill-green' },
  filling: { label: 'AI filling', tone: 'a6-pill-amber' },
};

const TICK_MS = 2100;
const FEED_SIZE = 4;
const BASE_COMPLETED = 38;

function feedWindow(tick) {
  const len = HERO_FEED.length;
  const items = [];
  for (let i = 0; i < FEED_SIZE; i += 1) {
    const eventIndex = (((tick - i) % len) + len) % len;
    items.push({
      ...HERO_FEED[eventIndex],
      key: `${tick - i}`,
      status: i === 0 ? 'working' : 'done',
    });
  }
  return items;
}

export default function AutopilotHero() {
  const reduceMotion = useReducedMotion();
  const [tick, setTick] = useState(FEED_SIZE);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const items = feedWindow(tick);
  const slotFilled = reduceMotion || tick % HERO_FEED.length >= 3 || tick >= HERO_FEED.length;
  const completed = BASE_COMPLETED + (reduceMotion ? 4 : tick - FEED_SIZE);

  return (
    <div className="a6-sim" role="img" aria-label="Simulated view of the Asha operating system running a dental office on autopilot: the schedule fills itself while AI agents verify insurance, file claims, post payments, and follow up with patients.">
      <div className="a6-sim-chrome" aria-hidden="true">
        <span className="a6-sim-dots">
          <span />
          <span />
          <span />
        </span>
        <span className="a6-sim-title">Asha OS · Riverside Dental</span>
        <span className="a6-sim-live">
          <span className="a6-sim-live-dot" />
          Autopilot on
        </span>
      </div>

      <div className="a6-sim-body" aria-hidden="true">
        <div className="a6-sim-col">
          <div className="a6-sim-col-label">
            Today's schedule
            <span className="a6-sim-count">Op 2 · Dr. Patel</span>
          </div>
          {HERO_SCHEDULE.map((slot) => {
            const isOpen = slot.status === 'filling' && !slotFilled;
            const isFilled = slot.status === 'filling' && slotFilled;
            const pill = isFilled
              ? SLOT_PILLS.booked
              : SLOT_PILLS[isOpen ? 'filling' : slot.status];
            return (
              <div
                key={slot.time}
                className={`a6-slot${isOpen ? ' is-open' : ''}${isFilled ? ' is-filled' : ''}`}
              >
                <span className="a6-slot-time">{slot.time}</span>
                <span className="a6-slot-patient">
                  {isFilled ? 'Crown · waitlist' : slot.patient}
                </span>
                <span className={`a6-slot-pill ${pill.tone}`}>{pill.label}</span>
              </div>
            );
          })}
        </div>

        <div className="a6-sim-col">
          <div className="a6-sim-col-label">
            AI working right now
            <span className="a6-sim-count">{completed} tasks done since 7:00 AM</span>
          </div>
          <div className="a6-feed">
            <AnimatePresence initial={false} mode="popLayout">
              {items.map((item) => {
                const Icon = item.status === 'done' ? Check : AGENT_ICONS[item.agent] || Phone;
                return (
                  <motion.div
                    key={item.key}
                    layout
                    initial={reduceMotion ? false : { opacity: 0, y: -16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={reduceMotion ? undefined : { opacity: 0, y: 14 }}
                    transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
                    className={`a6-feed-item is-${item.status}`}
                  >
                    <span className="a6-feed-icon">
                      <Icon size={15} strokeWidth={2.4} />
                    </span>
                    <span>
                      <span className="a6-feed-agent">{item.agent}</span>
                      <br />
                      <span className="a6-feed-text">
                        {item.status === 'working' ? item.working : item.done}
                      </span>
                    </span>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="a6-sim-footer" aria-hidden="true">
        <span className="a6-sim-loop">
          <RefreshCw size={14} strokeWidth={2.4} />
          ARIA&nbsp;<span>reasons → acts → observes</span>
        </span>
        <span>— dispatching specialist AI across the whole practice, around the clock.</span>
      </div>
    </div>
  );
}
